import { InvestmentDetailsInterface } from "../../../../../../models";
import { GetDealResponse, SearchCollaboratorsResponse } from "../../../../../../service/query";
import { convertDateToStringYYYYMMDD } from "../../../../../../utils";
import { mapInvestmentsToDealSpecificData as mapInvestmentsToDealSpecificDataShared } from "../../../../utils/dealSpecificDataUtils";

export type DealStructureTypeKey = "equity" | "landAcquisition" | "revenueShare" | "unitPurchase" | "loan";

type DealRelatedObject = GetDealResponse["relatedObjects"][number];
type CollaboratorDetails = SearchCollaboratorsResponse["results"][number];

const isExcludedDynamicField = (key: string): boolean => key === "description" || key === "relatedObjects";

const parseRelatedObjectUuids = (rawRelatedObjects: unknown): string[] => {
  if (!rawRelatedObjects) return [];
  if (Array.isArray(rawRelatedObjects)) {
    return rawRelatedObjects.map((obj) => (obj as { uuid?: string })?.uuid ?? "").filter(Boolean);
  }
  const uuid = (rawRelatedObjects as { uuid?: string })?.uuid;
  return uuid ? [uuid] : [];
};

const buildDynamicFields = (item: Record<string, unknown>): InvestmentDetailsInterface["fields"] => {
  return Object.entries(item).reduce<NonNullable<InvestmentDetailsInterface["fields"]>>((acc, [key, value]) => {
    if (isExcludedDynamicField(key)) return acc;

    if (Array.isArray(value)) {
      if (value.some((entry) => entry !== null && typeof entry === "object")) {
        (acc as Record<string, unknown>)[key] = value;
      } else {
        acc[key] = value;
      }
      return acc;
    }

    if (value === null) {
      acc[key] = null;
      return acc;
    }

    if ((value as unknown) instanceof Date) {
      acc[key] = convertDateToStringYYYYMMDD(value as unknown as Date);
      return acc;
    }

    if (value === undefined || typeof value === "object") return acc;

    acc[key] = value;
    return acc;
  }, {});
};

export const buildDealStructureItems = (
  typeKey: DealStructureTypeKey,
  items: string[] | null | undefined
): InvestmentDetailsInterface[] =>
  (items ?? []).map((jsonStr, index) => {
    const item = JSON.parse(jsonStr) as Record<string, unknown>;
    const relatedObjectUuids = parseRelatedObjectUuids(item.relatedObjects);

    return {
      key: `${typeKey}-${index}`,
      instrument: typeKey,
      associations: relatedObjectUuids,
      description: (item.description as string | null | undefined) ?? undefined,
      fields: buildDynamicFields(item),
    };
  });

export const getRelatedObjectDisplayName = (
  relatedObjectUuid: string,
  relatedObjectLookup: Map<string, DealRelatedObject>,
  collaborator: CollaboratorDetails | undefined
): string => {
  const match = relatedObjectLookup.get(relatedObjectUuid);
  if (!match) return relatedObjectUuid;

  if (match.objectType === "Project") {
    return match.project?.displayName ?? relatedObjectUuid;
  }

  if (match.objectType === "Landholding") {
    return match.landholding?.displayName ?? relatedObjectUuid;
  }

  if (match.objectType === "Collaborator") {
    if (collaborator?.uuid === relatedObjectUuid) return collaborator.name ?? relatedObjectUuid;
    return relatedObjectUuid;
  }

  return match.objectType ?? relatedObjectUuid;
};

export const mapInvestmentsToDealSpecificData = mapInvestmentsToDealSpecificDataShared;
